import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { X } from 'lucide-react'
import { useAppDispatch } from '../store/hooks'
import { copyApp } from '../store/appsSlice'
import './CopyAppModal.css'

interface CopyAppModalProps {
  sourceAppId: string
  sourceAppName: string
  onClose: () => void
}

function CopyAppModal({ sourceAppId, sourceAppName, onClose }: CopyAppModalProps) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [deployUrl, setDeployUrl] = useState('')
  const [instructions, setInstructions] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const modalRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement
    modalRef.current?.focus()

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
        return
      }
      if (e.key === 'Tab' && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])'
        )
        if (focusable.length === 0) return
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      previousFocusRef.current?.focus()
    }
  }, [onClose])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!deployUrl.trim() || !instructions.trim()) {
      setError('Both fields are required.')
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      const result = await dispatch(copyApp({
        sourceAppId,
        deployUrl: deployUrl.trim(),
        instructions: instructions.trim(),
      })).unwrap()
      navigate(`/apps/${result.appId}`)
    } catch (err) {
      setError((err as { message?: string })?.message || 'Failed to copy app')
      setSubmitting(false)
    }
  }

  return (
    <div className="copy-modal-overlay" data-testid="copy-app-modal-overlay" onClick={onClose}>
      <div
        className="copy-modal"
        data-testid="copy-app-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="copy-app-modal-title"
        ref={modalRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="copy-modal__header">
          <h2 className="copy-modal__title" id="copy-app-modal-title" data-testid="copy-app-modal-title">Copy App</h2>
          <button
            className="copy-modal__close"
            onClick={onClose}
            aria-label="Close dialog"
            data-testid="copy-app-modal-close"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>
        <p className="copy-modal__description">
          Create a copy of <strong>{sourceAppName}</strong> with a new deployment and database.
        </p>
        <form className="copy-modal__form" onSubmit={handleSubmit} noValidate>
          <label className="copy-modal__label" htmlFor="copy-deploy-url">
            Deploy URL
          </label>
          <input
            id="copy-deploy-url"
            className="copy-modal__input"
            type="text"
            placeholder="https://my-new-app.netlify.app"
            value={deployUrl}
            onChange={(e) => setDeployUrl(e.target.value)}
            data-testid="copy-app-deploy-url"
            disabled={submitting}
            aria-required="true"
          />
          <label className="copy-modal__label" htmlFor="copy-instructions">
            Changes &amp; Instructions
          </label>
          <textarea
            id="copy-instructions"
            className="copy-modal__textarea"
            placeholder="Describe any changes to make to the copied app..."
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            rows={4}
            data-testid="copy-app-instructions"
            disabled={submitting}
            aria-required="true"
          />
          {error && (
            <p className="copy-modal__error" role="alert" data-testid="copy-app-error">
              {error}
            </p>
          )}
          <div className="copy-modal__actions">
            <button
              type="button"
              className="copy-modal__btn copy-modal__btn--cancel"
              onClick={onClose}
              disabled={submitting}
              data-testid="copy-app-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="copy-modal__btn copy-modal__btn--submit"
              disabled={submitting}
              data-testid="copy-app-submit"
            >
              {submitting ? 'Copying...' : 'Copy App'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CopyAppModal
